/**
 * Constantes do módulo de questionário (Form Builder).
 *
 * Objetivo:
 * - Centralizar strings e metadados usados na persistência FHIR (Questionnaire).
 * - Manter o service e transformers livres de "stringly-typed".
 */

export const QUESTIONNAIRE_METADATA = {
  publisher: "Fluxmed",
  contact: [{ name: "contato@fluxmed.com.br" }],
  copyright: "fluxmed.com.br",
  defaultVersion: "1.0.0",
} as const;

/**
 * Identificador de organização usado no FHIR `Questionnaire.identifier`.
 *
 * Padrão:
 * - `identifier[0].system` => fixo
 * - `identifier[0].value`  => `Organization/{organizationId}`
 *
 * Mantido como constante para garantir que `create` e `search` usem o mesmo `system`.
 */
export const QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM =
  "http://fluxmed.com.br/fhir/identifier/questionnaire-organization";

/**
 * Extensão para tipo de uso do questionário (inclui categoria acompanhamento / `attendance`).
 */
export const QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL =
  "http://fluxmed.com.br/StructureDefinition/questionnaire-usage-type";

/**
 * Extensão para persistir o título do Questionnaire dentro do QuestionnaireResponse.
 *
 * Motivação:
 * - O Patient Summary pode retornar apenas `QuestionnaireResponse` sem o `Questionnaire`.
 * - Persistir o título evita lookup/enriquecimento em telas de listagem.
 */
export const QUESTIONNAIRE_TITLE_EXTENSION_URL =
  "http://fluxmed.com.br/StructureDefinition/questionnaire-title";

/**
 * Vincula QuestionnaireResponse a um Appointment no ehr-poster (mesmo contrato de
 * `PaymentNotice` e outros recursos Fluxmed que referenciam agendamento).
 */
export const QUESTIONNAIRE_RESPONSE_APPOINTMENT_REFERENCE_EXTENSION_URL =
  "http://fluxmed.com.br/fhir/StructureDefinition/AppointmentReference";

/**
 * Mapeamento usageType (domínio) -> code FHIR.
 */
export const USAGE_TYPE_TO_CODE: Record<
  "triage" | "preConsultation" | "postConsultation" | "attendance",
  string
> = {
  triage: "triage",
  preConsultation: "pre-consultation",
  postConsultation: "post-consultation",
  attendance: "attendance",
};

export const CODE_TO_USAGE_TYPE: Partial<
  Record<
    string,
    "triage" | "preConsultation" | "postConsultation" | "attendance"
  >
> = {
  triage: "triage",
  "pre-consultation": "preConsultation",
  "post-consultation": "postConsultation",
  attendance: "attendance",
};

/**
 * Mapeamento QuestionnaireQuestionType -> FHIR item.type.
 */
export const QUESTION_TYPE_TO_FHIR: Record<
  "shortText" | "longText" | "yesNo" | "multipleChoice",
  string
> = {
  shortText: "string",
  longText: "text",
  yesNo: "boolean",
  multipleChoice: "coding",
};

export const FHIR_TYPE_TO_QUESTION: Partial<
  Record<string, "shortText" | "longText" | "yesNo" | "multipleChoice">
> = {
  string: "shortText",
  text: "longText",
  boolean: "yesNo",
  coding: "multipleChoice",
};
